import { Fragment, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { ChevronDownIcon } from 'lucide-react';

import { cn } from '@/lib/utils';
import { navItemClasses } from '../navItemClasses';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { usePreferencesStore } from '@/shared/store/preferences';
import { useWorkspace } from '@/features/workspaces';
import { useNavViewer } from '../hooks/useNavViewer';
import {
  PRIMARY_NAV,
  SECONDARY_NAV,
  SECONDARY_NAV_LABEL,
  visibleNavItems,
  type NavItem,
} from '../navigation';
import { WorkflowsNavSection } from './WorkflowsNavSection';
import { NavRail } from './NavRail';
import { WorkspaceIdentity } from './WorkspaceIdentity';

/** Ties the Workflows row's disclosure to the list it opens. */
const WORKFLOWS_LIST_ID = 'sidebar-workflows-list';

export interface SidebarProps {
  workspaceId: string;
  /** Collapses to icons only. Driven by viewport width, not persisted. */
  collapsed?: boolean;
  className?: string;
}

function NavEntry({
  item,
  workspaceId,
  collapsed,
  count,
  disclosure,
}: {
  item: NavItem;
  workspaceId: string;
  collapsed: boolean;
  count?: number;
  /** Rendered beside the link, for an entry that also opens a sub-list. */
  disclosure?: ReactNode;
}) {
  const Icon = item.icon;
  const link = (
    <Link
      to={item.to}
      params={{ workspaceId }}
      activeOptions={{ exact: item.exact ?? false }}
      className={navItemClasses({ collapsed, className: 'min-w-0 flex-1' })}
    >
      <Icon aria-hidden="true" className="size-4 shrink-0" strokeWidth={1.5} />
      {collapsed ? (
        <span className="sr-only">{item.label}</span>
      ) : (
        <>
          <span className="flex-1 truncate">{item.label}</span>
          {typeof count === 'number' ? (
            // Decorative: the same figure is stated on the destination
            // itself, and folding it into the link's accessible name would
            // make that name change as data loads.
            <span
              aria-hidden="true"
              className="text-muted-foreground text-2xs tabular-nums"
            >
              {count}
            </span>
          ) : null}
        </>
      )}
    </Link>
  );
  return (
    <li className="flex items-center gap-0.5">
      {collapsed ? (
        // The shared tooltip, not a `title`: a title appears after the
        // browser's own delay, never on keyboard focus and never on touch, so a
        // collapsed rail read by keyboard was a column of unlabelled icons.
        <Tooltip>
          <TooltipTrigger render={link} />
          <TooltipContent side="right">{item.label}</TooltipContent>
        </Tooltip>
      ) : (
        link
      )}
      {collapsed ? null : disclosure}
    </li>
  );
}

/**
 * The control that folds a nav entry's sub-list away.
 *
 * Separate from the link on purpose: the row's label is an address and must
 * stay one — clicking "Workflows" goes to the workflows page. Folding twenty-four
 * workflows away is a different intent, and it gets its own target rather than
 * stealing the label's.
 */
function DisclosureButton({
  label,
  expanded,
  controls,
  onToggle,
}: {
  label: string;
  expanded: boolean;
  controls: string;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      aria-expanded={expanded}
      aria-controls={controls}
      aria-label={expanded ? `Collapse ${label}` : `Expand ${label}`}
      onClick={onToggle}
      className="text-sidebar-foreground/60 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-sidebar-ring shrink-0 rounded-md p-1.5 transition-colors focus-visible:ring-3 focus-visible:outline-none"
    >
      <ChevronDownIcon
        aria-hidden="true"
        strokeWidth={1.5}
        className={cn('size-4 transition-transform', !expanded && '-rotate-90')}
      />
    </button>
  );
}

export function Sidebar({
  workspaceId,
  collapsed = false,
  className,
}: SidebarProps) {
  // Counts come from the workspace aggregate the service already returns, so
  // they are real rather than decorative. While it loads there is no badge —
  // a placeholder number would be a small lie repeated on every screen.
  const { data: workspace } = useWorkspace(workspaceId);
  // Who the navigation is drawn for: owner, member, org admin (specs 072, 075, 188).
  // The same reading the grouped entries' tab strips use (spec 224), so a tab is
  // never offered on a different rule from the entry that holds it.
  const viewer = useNavViewer(workspaceId);
  const primaryNav = visibleNavItems(PRIMARY_NAV, viewer);
  const secondaryNav = visibleNavItems(SECONDARY_NAV, viewer);
  const workflowsExpanded = usePreferencesStore(
    (state) => state.workflowsExpanded,
  );
  const toggleWorkflows = usePreferencesStore((state) => state.toggleWorkflows);
  const counts: Record<string, number | undefined> = {
    // Every workflow, drafts included — the same figure the list this row folds
    // shows, and the same the page it links to shows (spec 094 FR-004). It used
    // to be accompanied by a separate `boards` count over the published subset,
    // and the two disagreeing about what the workspace held was half of the
    // confusion this feature removes.
    workflows: workspace?.workflows.length,
    // Every task in the workspace, which is exactly what the Tasks page lists —
    // not the board-eligible subset. A figure that disagreed with the length of
    // the list behind it would be worse than no figure.
    tasks: workspace?.tasks.length,
  };

  return (
    <NavRail
      collapsed={collapsed}
      className={className}
      context={
        <WorkspaceIdentity
          workspaceId={workspaceId}
          name={workspace?.name}
          workflowCount={workspace?.workflows.length}
          taskCount={workspace?.tasks.length}
          collapsed={collapsed}
        />
      }
    >
      <nav aria-label="Main" className="flex flex-col gap-1">
        <ul className="flex flex-col gap-0.5">
          {primaryNav.map((item) => (
            <Fragment key={item.to}>
              <NavEntry
                item={item}
                workspaceId={workspaceId}
                collapsed={collapsed}
                count={item.badge ? counts[item.badge] : undefined}
                disclosure={
                  item.to === '/workspaces/$workspaceId/workflows' ? (
                    <DisclosureButton
                      label="workflows"
                      expanded={workflowsExpanded}
                      controls={WORKFLOWS_LIST_ID}
                      onToggle={toggleWorkflows}
                    />
                  ) : undefined
                }
              />
              {/* One entry per workflow, listed under the Workflows entry itself
                  (BR-002-01) rather than only behind it. Rendered as a list item so
                  the nested list stays a valid child of this <ul>.

                  The disclosure moved here from the Boards row when the two
                  sections merged (spec 094). It keeps naming the list it controls,
                  so it stays a labelled disclosure rather than becoming a bare
                  chevron. */}
              {item.to === '/workspaces/$workspaceId/workflows' &&
              workflowsExpanded ? (
                <li>
                  <WorkflowsNavSection
                    id={WORKFLOWS_LIST_ID}
                    workspaceId={workspaceId}
                    collapsed={collapsed}
                  />
                </li>
              ) : null}
            </Fragment>
          ))}
        </ul>
      </nav>

      <nav aria-label={SECONDARY_NAV_LABEL} className="flex flex-col gap-1">
        {collapsed ? null : (
          <p className="text-muted-foreground px-2.5 text-2xs font-medium tracking-wide uppercase">
            {SECONDARY_NAV_LABEL}
          </p>
        )}
        <ul className="flex flex-col gap-0.5">
          {secondaryNav.map((item) => (
            <NavEntry
              key={item.to}
              item={item}
              workspaceId={workspaceId}
              collapsed={collapsed}
            />
          ))}
        </ul>
      </nav>
    </NavRail>
  );
}
